// src\components\account\widgets\widgetItem\MarqueeWidget.tsx
"use client"

import { Button } from "@/components/ui/button";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Plus, Trash2 } from "lucide-react";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import TextInput from "../../input/TextInput";
import FileUploadInput from "../../input/MultipleFileUploadInput";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface MarqueeWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const MarqueeWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: MarqueeWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    return (
        <div className="col-span-2 space-y-4">
            <div className="flex items-center gap-4">
                <div className="flex flex-col flex-1">
                    <label className="text-xs font-medium text-muted-foreground mb-1">سرعت حرکت</label>
                    <Controller
                        control={control}
                        name={`${name}.${widgetIndex}.speed`}
                        render={({ field }) => (
                            <Select value={field.value || "normal"} onValueChange={field.onChange} disabled={disabled}>
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="slow">آهسته</SelectItem>
                                    <SelectItem value="normal">معمولی</SelectItem>
                                    <SelectItem value="fast">سریع</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>
                <div className="flex flex-col flex-1">
                    <label className="text-xs font-medium text-muted-foreground mb-1">جهت حرکت</label>
                    <Controller
                        control={control}
                        name={`${name}.${widgetIndex}.direction`}
                        render={({ field }) => (
                            <Select value={field.value || "left"} onValueChange={field.onChange} disabled={disabled}>
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="left">به چپ</SelectItem>
                                    <SelectItem value="right">به راست</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                </div>
            </div>

            {fields.map((field, itemIndex) => (
                <div key={field.id} className="border rounded-md p-3 space-y-3">
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <TextInput
                                name={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                                label={`عبارت ${itemIndex + 1} (اختیاری اگه تصویر گذاشتی)`}
                                placeholder="مثلا: دست‌ساز و طبیعی"
                                register={register}
                                errors={errors}
                                disabled={disabled}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => fields.length > 1 && remove(itemIndex)}
                            disabled={fields.length <= 1 || disabled}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* به‌جای عبارت متنی می‌شه یه تصویر/لوگو هم گذاشت؛ اگه
                        تصویر پر بشه، تو نوار متحرک به‌جای متن نمایش داده می‌شه */}
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            تصویر / لوگو (اختیاری)
                        </label>
                        <Controller
                            control={control}
                            name={`${name}.${widgetIndex}.items.${itemIndex}.image`}
                            render={({ field: imageField }) => (
                                <FileUploadInput
                                    accept="image/*"
                                    disabled={disabled}
                                    onUploadSuccess={(image) => {
                                        if (!Array.isArray(image) && image) imageField.onChange(image);
                                    }}
                                    defaultImages={
                                        imageField.value
                                            ? [
                                                {
                                                    ...imageField.value,
                                                    name: imageField.value.name || '',
                                                    description: imageField.value.description || '',
                                                },
                                            ]
                                            : []
                                    }
                                    onDefaultRemove={() => imageField.onChange(undefined)}
                                />
                            )}
                        />
                    </div>
                </div>
            ))}

            <Button
                type="button"
                variant="outline"
                onClick={() => append({ title: "", image: undefined })}
                disabled={disabled}
            >
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " عبارت"}
            </Button>
        </div>
    );
};